import { useEffect, useMemo, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import { AdvancedDragModal, ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { JPBPriceIndexSource } from "./JPBState";
import { JPBManager, JPBPriceIndexFactorsTable } from "./JPBManager";

export type JPBPriceIndexSourceWindowParams = {
   source: JPBPriceIndexSource;
   onConfirm?: (source: JPBPriceIndexSource) => void;
};

const SOURCE_OPTIONS: Array<{ value: JPBPriceIndexSource; label: string }> = [
   { value: JPBPriceIndexSource.AgroCura, label: "AgroCura" },
   { value: JPBPriceIndexSource.Molbak, label: "Mølbak" },
   { value: JPBPriceIndexSource.None, label: "Ingen" }
];

function formatFactor(value: number | null): string {
   if (value === null || value === undefined || !Number.isFinite(value)) return "-";
   return value.toFixed(3);
}

export function JPBPriceIndexSourceWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const params = props.managedWindow.windowParams as JPBPriceIndexSourceWindowParams | undefined;
   const selectedSource = JPBManager.normalizePriceIndexSource(params?.source);
   const onConfirm = params?.onConfirm;

   const [draftSource, setDraftSource] = useState<JPBPriceIndexSource>(selectedSource);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [factorsTable, setFactorsTable] = useState<JPBPriceIndexFactorsTable | null>(null);

   useEffect(() => {
      setDraftSource(selectedSource);
   }, [selectedSource, props.managedWindow.updateCounter]);

   useEffect(() => {
      let cancelled = false;
      setLoading(true);
      setError(null);
      JPBManager.getPriceIndexFactorsTable(draftSource)
         .then((table) => {
            if (cancelled) return;
            setFactorsTable(table);
         })
         .catch((err: any) => {
            if (cancelled) return;
            setError(err?.message || String(err));
            setFactorsTable(null);
         })
         .finally(() => {
            if (cancelled) return;
            setLoading(false);
         });
      return () => {
         cancelled = true;
      };
   }, [draftSource]);

   const isMolbak = draftSource === JPBPriceIndexSource.Molbak;
   const rows = factorsTable?.rows ?? [];
   const periodKeys = factorsTable?.periodKeys ?? [];

   const emptyMessage = useMemo(() => {
      if (draftSource === JPBPriceIndexSource.None) {
         return Localization.getText("No price index is applied. Effective factor is 1.0 for all transactions.");
      }
      if (!rows.length) {
         return Localization.getText("No factors available.");
      }
      return null;
   }, [draftSource, rows.length]);

   const handleClose = () => {
      windowDispatch(closeWindow(props.managedWindow.id));
   };

   const handleApply = () => {
      onConfirm?.(draftSource);
      windowDispatch(closeWindow(props.managedWindow.id));
   };

   return (
      <AdvancedDragModal
         title={Localization.getText("Price index source")}
         variant="Big"
         window={props.managedWindow}
         onClose={handleClose}
         PosDefault={{
            left: "50%",
            top: "48%",
            transform: "translate(-50%, -50%)",
            width: "900px",
            height: "560px"
         }}
      >
         <ADModalBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
               <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <label className="GlassInputLabel" style={{ minWidth: 140 }}>
                     {Localization.getText("Price index source")}
                  </label>
                  <select
                     value={draftSource}
                     onChange={(e) => setDraftSource(JPBManager.normalizePriceIndexSource(e.target.value))}
                     style={{ minWidth: 220 }}
                  >
                     {SOURCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                           {option.label}
                        </option>
                     ))}
                  </select>
               </div>

               <div style={{ fontSize: "0.9em", color: "rgba(255,255,255,0.75)" }}>
                  {Localization.getFormattedText("Showing factors used by {source}.", {
                     source: JPBManager.getPriceIndexSourceLabel(draftSource)
                  })}
               </div>

               {loading && <div>{Localization.getText("Updating...")}</div>}
               {!loading && error && <div className="PropertyWarning">{error}</div>}
               {!loading && !error && emptyMessage && <div>{emptyMessage}</div>}

               {!loading && !error && !emptyMessage && (
                  <div style={{ overflowX: "auto", maxHeight: "360px", overflowY: "auto" }}>
                     <table className="table table-sm table-striped" style={{ minWidth: 720 }}>
                        <thead>
                           <tr>
                              <th>{Localization.getText("Landsdel")}</th>
                              {isMolbak && <th>{Localization.getText("Model")}</th>}
                              {isMolbak && <th>{Localization.getText("Avg tx count")}</th>}
                              {periodKeys.map((periodKey) => (
                                 <th key={periodKey} style={{ textAlign: "right" }}>{periodKey}</th>
                              ))}
                           </tr>
                        </thead>
                        <tbody>
                           {rows.map((row) => (
                              <tr key={row.landsdel}>
                                 <td>{row.landsdel}</td>
                                 {isMolbak && <td>{row.usesFittedModel ? Localization.getText("Fitted") : Localization.getText("Median")}</td>}
                                 {isMolbak && <td style={{ textAlign: "right" }}>{row.avgTxCount === null ? "-" : row.avgTxCount.toFixed(1)}</td>}
                                 {periodKeys.map((periodKey) => (
                                    <td key={`${row.landsdel}-${periodKey}`} style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                       {formatFactor(row.factorsByPeriod[periodKey] ?? null)}
                                    </td>
                                 ))}
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               )}
            </div>
         </ADModalBody>
         <ADModalFooter>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
               <GlassButton onClick={handleClose}>{Localization.getText("Cancel")}</GlassButton>
               <GlassButton onClick={handleApply}>{Localization.getText("OK")}</GlassButton>
            </div>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}
